import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { cn } from '@/utils/cn';
import { Button } from '@/components/ui/Button';
import { FieldLabel, parseFieldLabel } from '@/components/ui/FieldLabel';
import { PickerPopoverShell } from '@/components/ui/PickerPopoverShell';
import { TimePickerPanel } from '@/components/ui/TimePickerPanel';
import { usePickerPopover } from '@/components/ui/usePickerPopover';
import { displayTime, formatTimeValue, parseTimeValue } from '@/utils/dateInput';

export interface TimePickerProps {
  label?: string;
  error?: string;
  description?: string;
  hint?: string;
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  className?: string;
  id?: string;
  name?: string;
}

export function TimePicker({
  label,
  error,
  description: _description,
  hint: _hint,
  value = '',
  onChange,
  disabled,
  required,
  placeholder: placeholderProp,
  className,
  id,
  name,
}: TimePickerProps) {
  const { t } = useTranslation('common');
  const placeholder = placeholderProp ?? t('picker.selectTime');
  const autoId = useId();
  const fieldId = id ?? name ?? autoId;
  const { required: isRequired } = parseFieldLabel(label, required);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const { panelRef, panelCoords } = usePickerPopover(open, triggerRef, {
    estimatedHeight: 280,
    minWidth: 260,
    maxWidth: 300,
  });
  const parsed = parseTimeValue(value);
  const [hour, setHour] = useState(() =>
    parsed ? String(parsed.hour).padStart(2, '0') : '09',
  );
  const [minute, setMinute] = useState(() =>
    parsed ? String(parsed.minute).padStart(2, '0') : '00',
  );

  useEffect(() => {
    if (!open) return;
    const next = parseTimeValue(value);
    if (next) {
      setHour(String(next.hour).padStart(2, '0'));
      setMinute(String(next.minute).padStart(2, '0'));
    }
  }, [open, value]);

  useEffect(() => {
    const next = parseTimeValue(value);
    if (next) {
      setHour(String(next.hour).padStart(2, '0'));
      setMinute(String(next.minute).padStart(2, '0'));
    }
  }, [value]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [panelRef]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const apply = () => {
    onChange?.(formatTimeValue(Number(hour), Number(minute)));
    setOpen(false);
  };

  const display = displayTime(value);

  return (
    <div className={cn('relative min-w-0 w-full', className)} ref={rootRef}>
      <FieldLabel htmlFor={fieldId} label={label} required={required} />
      <button
        ref={triggerRef}
        type="button"
        id={fieldId}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={cn(
          'input flex w-full min-w-0 items-center gap-2 text-left',
          !display && 'text-slate-500 dark:text-slate-400',
          display && 'text-slate-900 dark:text-white',
          error && 'input-error',
          disabled && 'cursor-not-allowed opacity-60',
        )}
      >
        <ScheduleIcon className="shrink-0 text-primary-600 dark:text-primary-400" sx={{ fontSize: 18 }} />
        <span className="truncate">{display || placeholder}</span>
      </button>
      {name && <input type="hidden" name={name} value={value} required={isRequired} readOnly />}
      {open &&
        panelCoords &&
        createPortal(
          <div
            ref={panelRef}
            className="fixed z-[300]"
            style={{ top: panelCoords.top, left: panelCoords.left, width: panelCoords.width }}
          >
            <PickerPopoverShell
              footer={
                <>
                  <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)}>
                    {t('actions.cancel', { defaultValue: 'Cancel' })}
                  </Button>
                  <Button type="button" size="sm" onClick={apply}>
                    {t('actions.apply', { defaultValue: 'Apply' })}
                  </Button>
                </>
              }
            >
              <TimePickerPanel
                embedded
                hour={hour}
                minute={minute}
                onHourChange={setHour}
                onMinuteChange={setMinute}
                showApply={false}
              />
            </PickerPopoverShell>
          </div>,
          document.body,
        )}
      {error && (
        <p className="mt-1.5 text-xs text-error-700 dark:text-red-400" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export function defaultTimeValue(hour = 9, minute = 0): string {
  return formatTimeValue(hour, minute);
}
